import IndexLayout from '../layouts';
import Wrapper from '../components/Wrapper';
import LazyLoad from 'react-lazyload';

import SiteNav from '../components/header/SiteNav';
import { SiteHeader, outer, inner, SiteMain } from '../styles/shared';
import * as React from 'react';
import { css } from '@emotion/core';
import { PostFullHeader, PostFullTitle, NoImage, PostFull } from '../templates/post';
import { PostFullContent } from '../components/PostContent';
import Footer from '../components/Footer';
import Helmet from 'react-helmet';

import BackToTop from '../components/BackToTop';

const COImg = css`
  max-width: 200px;
  @media (max-width: 640px) {
    max-width: 140px;
  }
`;

const PageTemplate = css`
  .site-main {
    background #fff;
    padding-bottom: 4vw;
  }

  .post-content {
    max-width: 1000px;
    margin: 0 auto;
  }
  .post-content .large-key .key {
    font-size: 3rem;
    padding: 15px;
    margin-top: 20px;
    display: inline-block;
    border-radius: 6px;
  }
  .large-key {
    display: grid;
    grid-template-columns: 1fr 1fr 1fr;
    margin-top: 4rem;
  }

  @media(max-width: 400px) {
    .large-key {
      grid-template-columns: 1fr 1fr;
    }
  }
`;

const KBShortcuts: React.FunctionComponent = () => (
  <IndexLayout>
    <Helmet>
      <title>Capture One Pro Keyboard Shortcut Reference | Giggster Guide</title>
      <meta
        name="description"
        content="A sweet reference page for the most useful keyboard shortcuts in Capture One Pro. Give your RAW photo editing workflow a boost!"
      />
    </Helmet>
    <Wrapper css={PageTemplate}>
      <header css={[outer, SiteHeader]}>
        <div css={inner}>
          <SiteNav />
        </div>
      </header>
      <main id="site-main" className="site-main" css={[SiteMain, outer]}>
        <article className="post page" css={[PostFull, NoImage]}>
          <PostFullHeader>
            <PostFullTitle>Capture One Pro Keyboard Shortcuts ⌨️</PostFullTitle>
          </PostFullHeader>

          <PostFullContent className="post-full-content">
            <div className="post-content">
              <p>
                A reference of some of the most common &amp; useful default Capture One Pro
                shortcuts (hotkeys). Getting used to using shortcuts in Capture One should greatly
                speed up your RAW photo editing workflow!
              </p>

              <section class="info-box">
                To view the full list of keyboard shortcuts and to customize some of the shortcuts,
                go to the <strong>Edit</strong> menu and then to{' '}
                <strong>Edit Keyboard Shortcuts</strong>.
              </section>

              <p>
                <span className="show-for-small-only">
                  PC users, replace <span className="key">Opt</span> with{' '}
                  <span className="key">Alt</span>, and replace <span className="key">Cmd</span>{' '}
                  with <span className="key">Ctrl</span>.
                </span>
              </p>

              <h2 className="text-center">Cursor Tools</h2>

              <div className="row large-key">
                <div className="small-4 columns text-center">
                  <h4>
                    <span className="move-tool">Selection</span>
                  </h4>
                  <span className="key">V</span>
                </div>

                <div className="small-4 columns text-center">
                  <h4>
                    <span className="marquee">Navigation</span>
                  </h4>
                  <span className="key">H</span>
                </div>

                <div className="small-4 columns text-center">
                  <h4>
                    <span>Loupe</span>
                  </h4>
                  <span className="key">P</span>
                </div>
              </div>

              <div className="row large-key t30">
                <div className="small-4 columns text-center">
                  <h4>
                    <span className="magic-wand">Zoom In/Out</span>
                  </h4>
                  <span className="key">Z</span>
                </div>

                <div className="small-4 columns text-center">
                  <h4>
                    <span className="crop">Crop</span>
                  </h4>
                  <span className="key">C</span>
                </div>

                <div className="small-4 columns text-center">
                  <h4>
                    <span className="eyedropper">Rotation</span>
                  </h4>
                  <span className="key">R</span>
                </div>
              </div>

              <div className="row large-key t30">
                <div className="small-4 columns text-center">
                  <h4>
                    <span className="healing">Keystone</span>
                  </h4>
                  <span className="key">K</span>
                </div>

                <div className="small-4 columns text-center">
                  <h4>
                    <span className="brush">Spot</span>
                  </h4>
                  <span className="key">O</span>
                </div>

                <div className="small-4 columns text-center">
                  <h4>
                    <span className="stamp">Draw Mask</span>
                  </h4>
                  <span className="key">B</span>
                </div>
              </div>

              <div className="row large-key t30">
                <div className="small-4 columns text-center">
                  <h4>
                    <span>Erase Mask</span>
                  </h4>
                  <span className="key">E</span>
                </div>

                <div className="small-4 columns text-center">
                  <h4>
                    <span className="eraser">Linear Gradient Mask</span>
                  </h4>
                  <span className="key">G</span>
                </div>

                <div className="small-4 columns text-center">
                  <h4>
                    <span className="gradient">Radial Gradient Mask</span>
                  </h4>
                  <span className="key">T</span>
                </div>
              </div>

              <div className="row large-key t30">
                <div className="small-4 columns text-center">
                  <h4>
                    <span>Toggle Mask Display</span>
                  </h4>
                  <span className="key">M</span>
                </div>

                <div className="small-4 columns text-center">
                  <h4>
                    <span className="pen">White Balance Picker</span>
                  </h4>
                  <span className="key">W</span>
                </div>

                <div className="small-4 columns text-center">
                  <h4>
                    <span className="text-tool">Copy/Apply Adjustments</span>
                  </h4>
                  <span className="key">A</span>
                </div>
              </div>

              <div className="row large-key t30">
                <div className="small-4 columns text-center">
                  <h4>
                    <span className="selection">Draw Annotation</span>
                  </h4>
                  <span className="key">I</span>
                </div>

                <div className="small-4 columns text-center">
                  <h4>
                    <span className="shape">Erase Annotation</span>
                  </h4>
                  <span className="key">Y</span>
                </div>
              </div>

              <hr />

              <h2 className="text-center">Workflow</h2>

              <div className="row large-key t30">
                <div className="small-4 columns text-center">
                  <h4>New Session</h4>
                  <span className="key">
                    Cmd <span className="key-alternate">(Ctrl)</span>
                  </span>
                  <br />
                  <span className="k-plus">+</span>
                  <br />
                  <span className="key">N</span>
                </div>

                <div className="small-4 columns text-center">
                  <h4>New Catalog</h4>
                  <span className="key">Shift</span>
                  <br />
                  <span className="k-plus">+</span>
                  <br />
                  <span className="key">
                    Cmd <span className="key-alternate">(Ctrl)</span>
                  </span>
                  <br />
                  <span className="k-plus">+</span>
                  <br />
                  <span className="key">N</span>
                </div>

                <div className="small-4 columns text-center">
                  <h4>Process Recipes</h4>
                  <span className="key">
                    Cmd <span className="key-alternate">(Ctrl)</span>
                  </span>
                  <br />
                  <span className="k-plus">+</span>
                  <br />
                  <span className="key">D</span>
                </div>
              </div>

              <div className="row large-key t30">
                <div className="small-4 columns text-center">
                  <h4>New Variant</h4>
                  <span className="key">F2</span>
                </div>

                <div className="small-6 columns text-center">
                  <h4>Clone Variant</h4>
                  <span className="key">F3</span>
                </div>

                <div className="small-6 columns text-center">
                  <h4>Move to Selects</h4>
                  <span className="key">
                    Cmd <span className="key-alternate">(Ctrl)</span>
                  </span>
                  <br />
                  <span className="k-plus">+</span>
                  <br />
                  <span className="key">J</span>
                </div>
              </div>

              <div className="row large-key t30">
                <div className="small-4 columns text-center">
                  <h4>New Variant</h4>
                  <span className="key">F2</span>
                </div>

                <div className="small-6 columns text-center">
                  <h4>Clone Variant</h4>
                  <span className="key">F3</span>
                </div>

                <div className="small-6 columns text-center">
                  <h4>Move to Selects</h4>
                  <span className="key">
                    Cmd <span className="key-alternate">(Ctrl)</span>
                  </span>
                  <br />
                  <span className="k-plus">+</span>
                  <br />
                  <span className="key">J</span>
                </div>
              </div>

              <hr />

              <h2 className="text-center">Adjustments</h2>

              <div className="row large-key">
                <div className="small-4 columns text-center">
                  <h4>Reset All Adjustments</h4>
                  <span className="key">
                    Cmd <span className="key-alternate">(Ctrl)</span>
                  </span>
                  <br />
                  <span className="k-plus">+</span>
                  <br />
                  <span className="key">R</span>
                </div>

                <div className="small-4 columns text-center">
                  <h4>Copy Adjustments</h4>
                  <span className="key">Shift</span>
                  <br />
                  <span className="k-plus">+</span>
                  <br />
                  <span className="key">
                    Cmd <span className="key-alternate">(Ctrl)</span>
                  </span>
                  <br />
                  <span className="k-plus">+</span>
                  <br />
                  <span className="key">C</span>
                </div>

                <div className="small-4 columns text-center">
                  <h4>Apply Adjustments</h4>
                  <span className="key">Shift</span>
                  <br />
                  <span className="k-plus">+</span>
                  <br />
                  <span className="key">
                    Cmd <span className="key-alternate">(Ctrl)</span>
                  </span>
                  <br />
                  <span className="k-plus">+</span>
                  <br />
                  <span className="key">V</span>
                </div>
              </div>

              <hr />

              <h2 className="text-center">Exposure / Contrast / white Balance</h2>

              <div className="row large-key">
                <div className="small-4 columns text-center">
                  <h4>Increase Exposure</h4>
                  <span className="key">Ctrl</span>
                  <br />
                  <span className="k-plus">+</span>
                  <br />
                  <span className="key">Cmd</span>
                  <br />
                  <span className="k-plus">+</span>
                  <br />
                  <span className="key">+</span>
                </div>

                <div className="small-4 columns text-center">
                  <h4>Decrease Exposure</h4>
                  <span className="key">Ctrl</span>
                  <br />
                  <span className="k-plus">+</span>
                  <br />
                  <span className="key">Cmd</span>
                  <br />
                  <span className="k-plus">+</span>
                  <br />
                  <span className="key">-</span>
                </div>

                <div className="small-4 columns text-center">
                  <h4>Increase Contrast</h4>
                  <span className="key">Ctrl</span>
                  <br />
                  <span className="k-plus">+</span>
                  <br />
                  <span className="key">Shift</span>
                  <br />
                  <span className="k-plus">+</span>
                  <br />
                  <span className="key">Cmd</span>
                  <br />
                  <span className="k-plus">+</span>
                  <br />
                  <span className="key">+</span>
                </div>
              </div>

              <div className="row large-key">
                <div className="small-4 columns text-center">
                  <h4>Decrease Contrast</h4>
                  <span className="key">Ctrl</span>
                  <br />
                  <span className="k-plus">+</span>
                  <br />
                  <span className="key">Shift</span>
                  <br />
                  <span className="k-plus">+</span>
                  <br />
                  <span className="key">Cmd</span>
                  <br />
                  <span className="k-plus">+</span>
                  <br />
                  <span className="key">-</span>
                </div>

                <div className="small-4 columns text-center">
                  <h4>Increase Color Temperature</h4>
                  <span className="key">Shift</span>
                  <br />
                  <span className="k-plus">+</span>
                  <br />
                  <span className="key">+</span>
                </div>

                <div className="small-4 columns text-center">
                  <h4>Decrease Color Temperature</h4>
                  <span className="key">Shift</span>
                  <br />
                  <span className="k-plus">+</span>
                  <br />
                  <span className="key">-</span>
                </div>
              </div>
            </div>
          </PostFullContent>
        </article>
      </main>
      <BackToTop />
      <Footer />
    </Wrapper>
  </IndexLayout>
);

export default KBShortcuts;
